/**
 * Schedule of Rates - export to and import from a formatted Excel sheet (0231).
 *
 * For the System Administrator Mrinmoy Debnath only: the buttons ask the
 * database (fn_is_superadmin) whether to show themselves, and the import
 * function refuses everybody else whatever this screen does.
 *
 * EXPORT  a workbook with three sheets - "Read me" (how to fill it in), the
 *         "Schedule of Rates" itself (one row per item, every field, drop-downs
 *         on the coded columns) and a hidden "Lists" sheet those drop-downs
 *         read from (units, SAC codes, natures, status, rate basis, yes/no).
 * IMPORT  the same sheet back: matched by Item Code, a new code adds an item,
 *         an existing one updates it, nothing is deleted. The file is CHECKED
 *         first (rpc_work_items_import with p_apply = false) and the preview
 *         shows every new item, every changed field old -> new, and every row
 *         with an error. Confirm applies all of it together, or nothing.
 */
import { useEffect, useRef, useState } from 'react'
import { Download, FileSpreadsheet, Upload } from 'lucide-react'
import { Alert, Modal, useToast } from '@/components/ui'
import { dbMessage } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { localIso } from '@/lib/format'
import type { WiRow } from '../pages/ScheduleOfRates'

/** The sheet's columns, in order. The header text is how the import finds each one. */
const COLS: { key: string; header: string; width: number; get: (r: WiRow) => unknown }[] = [
  { key: 'item_code', header: 'Item Code', width: 16, get: (r) => r.item_code },
  { key: 'item_name', header: 'Item Name', width: 48, get: (r) => r.item_name },
  { key: 'uom', header: 'Unit', width: 9, get: (r) => r.uom },
  { key: 'category', header: 'Trade (Category)', width: 24, get: (r) => r.category },
  { key: 'sub_category', header: 'Sub-trade', width: 24, get: (r) => r.sub_category },
  { key: 'nature', header: 'Nature', width: 18, get: (r) => r.default_nature },
  { key: 'sac_code', header: 'SAC Code', width: 11, get: (r) => r.sac_code },
  { key: 'gst_rate_percent', header: 'GST %', width: 8, get: (r) => (r.gst_rate_percent == null ? null : Number(r.gst_rate_percent)) },
  { key: 'standard_rate', header: 'Standard Rate (Rs)', width: 14, get: (r) => (r.standard_rate == null ? null : Number(r.standard_rate)) },
  { key: 'measurement_rule', header: 'Measurement Rule', width: 48, get: (r) => r.measurement_rule },
  { key: 'description', header: 'Description', width: 36, get: (r) => r.description },
  { key: 'status', header: 'Status', width: 10, get: (r) => (r.is_active ? 'Active' : 'Inactive') },
  { key: 'hire_rate_basis', header: 'Hire Rate Basis', width: 14, get: (r) => r.hire_rate_basis ?? null },
  { key: 'hire_quoted_rate', header: 'Hire Quoted Rate (Rs)', width: 16, get: (r) => (r.hire_quoted_rate == null ? null : Number(r.hire_quoted_rate)) },
  { key: 'hire_by_pieces', header: 'Hired By Pieces', width: 13, get: (r) => (r.default_nature === 'EQUIPMENT_HIRE' ? (r.hire_by_pieces ? 'Yes' : 'No') : null) },
]
const NATURES = ['WORKS_CONTRACT', 'PURE_LABOUR', 'MANPOWER_SUPPLY', 'EQUIPMENT_HIRE']
const SHEET = 'Schedule of Rates'
const norm = (s: unknown) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

function download(name: string, content: BlobPart) {
  const url = URL.createObjectURL(new Blob([content], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
  const a = document.createElement('a')
  a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

/** A cell's value as plain text: numbers, rich text, formula results and dates all flattened. */
function cellText(v: any): string {
  if (v == null) return ''
  if (typeof v === 'object') {
    if (v instanceof Date) return localIso(v)
    if ('result' in v) return cellText(v.result)
    if ('richText' in v) return (v.richText as any[]).map((t) => t.text).join('')
    if ('text' in v) return String(v.text)
    return ''
  }
  return String(v)
}

let superCache: { uid: string; yes: boolean } | null = null
/** Is the person signed in the superadmin? The database decides (fn_is_superadmin). */
export function useIsSuperadmin(uid: string | undefined): boolean {
  const [yes, setYes] = useState(() => !!uid && superCache?.uid === uid && superCache.yes)
  useEffect(() => {
    if (!uid) { setYes(false); return }
    if (superCache?.uid === uid) { setYes(superCache.yes); return }
    let live = true
    void supabase.rpc('fn_is_superadmin').then(({ data }) => {
      superCache = { uid, yes: data === true }
      if (live) setYes(data === true)
    })
    return () => { live = false }
  }, [uid])
  return yes
}

interface CheckResult {
  rows: number; added: number; updated: number; unchanged: number
  errors: { row: number; item_code: string | null; problems: string[] }[]
  changes: { row: number; item_code: string; kind: 'NEW' | 'CHANGED'; item_name: string; fields?: Record<string, [unknown, unknown]> }[]
}

export function SorExcelButtons({ rows, onImported }: { rows: WiRow[]; onImported: () => void }) {
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<'' | 'export' | 'check' | 'apply'>('')
  const [file, setFile] = useState<{ name: string; rows: Record<string, string>[] } | null>(null)
  const [check, setCheck] = useState<CheckResult | null>(null)

  // ---- export --------------------------------------------------------------
  async function exportSheet() {
    setBusy('export')
    try {
      const [{ data: uoms }, { data: sacs }] = await Promise.all([
        supabase.from('uoms').select('code, description').eq('is_active', true).order('code'),
        supabase.from('sac_codes').select('code, description, gst_rate_percent').eq('is_active', true).order('code'),
      ])
      const ExcelJS = (await import('exceljs')).default
      const wb = new ExcelJS.Workbook()
      wb.creator = 'OVAL IMS'

      // Read me
      const rd = wb.addWorksheet('Read me')
      rd.getColumn(1).width = 110
      const lines: [string, boolean?][] = [
        ['OVAL IMS - Schedule of Rates (Service Procurement)', true],
        [`Exported ${localIso()} - ${rows.length} items.`],
        [''],
        ['HOW TO USE THIS FILE', true],
        ['1. Change rows on the "Schedule of Rates" sheet, or add new rows at the bottom. Do not rename or move the column headings.'],
        ['2. Item Code is the key. A code already in IMS UPDATES that item; a new code ADDS an item (IMS stores it as WI-<CODE>).'],
        ['3. Nothing is ever deleted. To retire an item set its Status to Inactive. Items you leave out of the file are not touched.'],
        ['4. Required on every row: Item Code, Item Name, Unit, Trade, Nature, SAC Code, Measurement Rule.'],
        ['5. Unit, Nature, SAC Code, Status, Hire Rate Basis and Hired By Pieces have drop-downs - pick from them.'],
        ['6. GST % may be left blank: the SAC code\'s GST slab is used.'],
        ['7. Hire items (Nature EQUIPMENT_HIRE): Unit must be DAY; fill Hire Rate Basis (MONTH or DAY), Hire Quoted Rate and Hired By Pieces.'],
        ['   Their Standard Rate is worked out by IMS from the quoted rate (a month is 30 days) - what you type there is replaced.'],
        ['   For every other item the three hire columns are ignored.'],
        ['8. In IMS: Service Procurement > Schedule of Rates > Import from Excel. You first see a preview of every new item, every'],
        ['   changed field (old -> new) and every row with an error. Nothing changes until you press Confirm, and then all rows'],
        ['   change together. A file with any error cannot be confirmed - correct it and check it again.'],
        ['9. A rate here is a GUIDE (indicative). Changing it does not change any work order already placed.'],
      ]
      for (const [t, bold] of lines) {
        const row = rd.addRow([t])
        if (bold) row.font = { bold: true, size: t.startsWith('OVAL') ? 14 : 11 }
      }

      // Lists (hidden), for the drop-downs
      const ls = wb.addWorksheet('Lists')
      const listCols: [string, string[]][] = [
        ['Units', (uoms ?? []).map((u: any) => u.code)],
        ['SAC Codes', (sacs ?? []).map((s: any) => s.code)],
        ['Nature', NATURES],
        ['Status', ['Active', 'Inactive']],
        ['Hire Rate Basis', ['MONTH', 'DAY']],
        ['Yes / No', ['Yes', 'No']],
      ]
      listCols.forEach(([title, vals], i) => {
        ls.getCell(1, i + 1).value = title
        vals.forEach((v, k) => { ls.getCell(k + 2, i + 1).value = v })
      })
      ls.state = 'hidden'
      const listRef = (i: number) => {
        const col = String.fromCharCode(65 + i)
        return `Lists!$${col}$2:$${col}$${Math.max(2, listCols[i][1].length + 1)}`
      }

      // The schedule
      const ws = wb.addWorksheet(SHEET, { views: [{ state: 'frozen', ySplit: 1, xSplit: 2 }] })
      ws.columns = COLS.map((c) => ({ header: c.header, key: c.key, width: c.width }))
      const head = ws.getRow(1)
      head.font = { bold: true, color: { argb: 'FFFFFFFF' } }
      head.alignment = { vertical: 'middle', wrapText: true }
      head.height = 30
      head.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3C55A5' } }
        cell.border = { bottom: { style: 'thin', color: { argb: 'FF17211F' } } }
      })
      const sorted = [...rows].sort((a, b) => (a.category ?? '').localeCompare(b.category ?? '') || a.item_code.localeCompare(b.item_code))
      for (const r of sorted) {
        ws.addRow(Object.fromEntries(COLS.map((c) => [c.key, c.get(r) ?? null])))
      }
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: COLS.length } }
      for (const k of ['standard_rate', 'hire_quoted_rate']) ws.getColumn(k).numFmt = '#,##,##0.00'
      ws.getColumn('gst_rate_percent').numFmt = '0.##'
      for (const k of ['item_name', 'measurement_rule', 'description']) ws.getColumn(k).alignment = { wrapText: true, vertical: 'top' }

      // Drop-downs on the coded columns, for every row plus room for 500 new ones.
      const last = sorted.length + 501
      const dd: [string, number][] = [['uom', 0], ['sac_code', 1], ['nature', 2], ['status', 3], ['hire_rate_basis', 4], ['hire_by_pieces', 5]]
      for (const [key, li] of dd) {
        const col = COLS.findIndex((c) => c.key === key) + 1
        for (let rr = 2; rr <= last; rr++) {
          ws.getCell(rr, col).dataValidation = {
            type: 'list', allowBlank: true, formulae: [listRef(li)],
            showErrorMessage: true, errorTitle: 'Pick from the list',
            error: `Choose a value from the drop-down (${listCols[li][0]}).`,
          }
        }
      }
      wb.views = [{ x: 0, y: 0, width: 20000, height: 12000, firstSheet: 0, activeTab: 2, visibility: 'visible' }]

      const buf = await wb.xlsx.writeBuffer()
      download(`OVAL-Schedule-of-Rates-${localIso()}.xlsx`, buf)
    } catch (e) {
      toast.error(dbMessage(e, 'The Schedule of Rates could not be exported.'))
    } finally {
      setBusy('')
    }
  }

  // ---- import: read the file, then CHECK it ----------------------------------
  async function readFile(f: File) {
    setBusy('check')
    try {
      const ExcelJS = (await import('exceljs')).default
      const wb = new ExcelJS.Workbook()
      await wb.xlsx.load(await f.arrayBuffer())
      const ws = wb.getWorksheet(SHEET) ?? wb.worksheets.find((s) => s.state !== 'hidden' && norm(s.name) !== 'readme')
      if (!ws) throw new Error(`The file has no "${SHEET}" sheet.`)
      // Find each column by its heading.
      const where: Record<string, number> = {}
      ws.getRow(1).eachCell((cell, col) => {
        const h = norm(cellText(cell.value))
        const c = COLS.find((x) => norm(x.header) === h || norm(x.key) === h)
        if (c) where[c.key] = col
      })
      const missing = ['item_code', 'item_name', 'uom', 'category', 'nature', 'sac_code', 'measurement_rule']
        .filter((k) => !where[k]).map((k) => COLS.find((c) => c.key === k)!.header)
      if (missing.length) throw new Error(`These column headings are missing: ${missing.join(', ')}. Use the exported file as the template.`)

      const out: Record<string, string>[] = []
      ws.eachRow({ includeEmpty: false }, (row, rn) => {
        if (rn === 1) return
        const rec: Record<string, string> = { row: String(rn) }
        for (const c of COLS) rec[c.key] = where[c.key] ? cellText(row.getCell(where[c.key]).value).trim() : ''
        if (COLS.some((c) => rec[c.key] !== '')) out.push(rec)
      })
      if (!out.length) throw new Error('The sheet has no rows under the headings.')

      const { data, error } = await supabase.rpc('rpc_work_items_import', { p_file_name: f.name, p_rows: out, p_apply: false })
      if (error) throw error
      setFile({ name: f.name, rows: out })
      setCheck(data as CheckResult)
    } catch (e) {
      toast.error(dbMessage(e, 'The file could not be read.'))
    } finally {
      setBusy('')
    }
  }

  async function apply() {
    if (!file) return
    setBusy('apply')
    const { data, error } = await supabase.rpc('rpc_work_items_import', { p_file_name: file.name, p_rows: file.rows, p_apply: true })
    setBusy('')
    if (error) { toast.error(dbMessage(error, 'The import was not applied.')); return }
    const d = data as CheckResult
    toast.success(`Imported: ${d.added} added, ${d.updated} updated, ${d.unchanged} unchanged.`)
    setFile(null); setCheck(null)
    onImported()
  }

  const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v))
  const changed = check?.changes.filter((c) => c.kind === 'CHANGED') ?? []
  const added = check?.changes.filter((c) => c.kind === 'NEW') ?? []
  const SHOW = 300

  return (
    <>
      <button className="btn-secondary" disabled={!!busy} onClick={exportSheet}>
        <Download className="h-4 w-4" /> {busy === 'export' ? 'Exporting…' : 'Export to Excel'}
      </button>
      <button className="btn-secondary" disabled={!!busy} onClick={() => fileRef.current?.click()}>
        <Upload className="h-4 w-4" /> {busy === 'check' ? 'Checking…' : 'Import from Excel'}
      </button>
      <input ref={fileRef} type="file" className="hidden"
             accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void readFile(f) }} />

      <Modal open={!!check} onClose={() => { if (busy !== 'apply') { setCheck(null); setFile(null) } }} wide
             title="Import the Schedule of Rates - check before it changes"
             subtitle={file ? `${file.name} · ${check?.rows ?? 0} rows read` : undefined}
             footer={<>
               <button className="btn-secondary" disabled={busy === 'apply'} onClick={() => { setCheck(null); setFile(null) }}>Cancel</button>
               <button className="btn-primary" disabled={busy === 'apply' || !check || check.errors.length > 0 || (check.added + check.updated) === 0}
                       onClick={apply}>
                 <FileSpreadsheet className="h-4 w-4" /> {busy === 'apply' ? 'Importing…' : `Confirm: add ${check?.added ?? 0}, update ${check?.updated ?? 0}`}
               </button>
             </>}>
        {check && (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-4">
              <Tile label="New items" value={check.added} tone="text-teal-700" />
              <Tile label="Items changed" value={check.updated} tone="text-oval-800" />
              <Tile label="Unchanged" value={check.unchanged} tone="text-slate-600" />
              <Tile label="Rows with errors" value={check.errors.length} tone={check.errors.length ? 'text-red-700' : 'text-slate-600'} />
            </div>
            {check.errors.length > 0 && (
              <Alert tone="error" title="Correct these rows in the file, then import it again">
                Nothing can be confirmed while any row has an error - the whole file goes in together.
              </Alert>
            )}
            {check.errors.length === 0 && check.added + check.updated === 0 && (
              <Alert tone="info" title="Nothing to change">Every row matches the Schedule of Rates already.</Alert>
            )}

            {check.errors.length > 0 && (
              <Section title={`Errors (${check.errors.length})`}>
                <table className="tbl"><thead><tr><th>Excel row</th><th>Item code</th><th>What is wrong</th></tr></thead>
                  <tbody>{check.errors.slice(0, SHOW).map((e, i) => (
                    <tr key={i}><td className="text-xs">{e.row}</td><td className="font-mono text-xs">{e.item_code ?? '—'}</td>
                      <td className="text-xs text-red-700">{e.problems.join('; ')}</td></tr>))}</tbody></table>
              </Section>
            )}
            {added.length > 0 && (
              <Section title={`New items (${added.length})`}>
                <table className="tbl"><thead><tr><th>Excel row</th><th>Item code (as stored)</th><th>Item name</th></tr></thead>
                  <tbody>{added.slice(0, SHOW).map((c, i) => (
                    <tr key={i}><td className="text-xs">{c.row}</td><td className="font-mono text-xs">{c.item_code}</td>
                      <td className="text-xs">{c.item_name}</td></tr>))}</tbody></table>
              </Section>
            )}
            {changed.length > 0 && (
              <Section title={`Changes (${changed.length} items)`}>
                <table className="tbl"><thead><tr><th>Item code</th><th>Field</th><th>Now</th><th>Will be</th></tr></thead>
                  <tbody>{changed.slice(0, SHOW).flatMap((c) => Object.entries(c.fields ?? {}).map(([f, [a, b]], k) => (
                    <tr key={`${c.item_code}-${f}`}>
                      <td className="font-mono text-xs">{k === 0 ? c.item_code : ''}</td>
                      <td className="text-xs">{f}</td>
                      <td className="max-w-[18rem] text-xs text-slate-500">{fmt(a)}</td>
                      <td className="max-w-[18rem] text-xs font-semibold">{fmt(b)}</td>
                    </tr>)))}</tbody></table>
              </Section>
            )}
            {(check.errors.length > SHOW || added.length > SHOW || changed.length > SHOW) && (
              <p className="text-xs text-slate-500">Only the first {SHOW} of each list are shown; all of them are applied.</p>
            )}
          </div>
        )}
      </Modal>
    </>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <div className="table-wrap max-h-72 overflow-y-auto">{children}</div>
    </div>
  )
}
